import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ClinicGiftCards } from '@models/gift-cards/clinic-gift-cards';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { ClinicsService } from '@services/clinics.service';
import { GiftCardsService } from '@services/gift-cards.service';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { CommunicationType } from '@models/communications/communication-type';
import { CommunicationStatus } from '@models/communications/communication-status';

@Component({
  selector: 'app-gift-cards-overview',
  templateUrl: './gift-cards-overview.component.html',
  styleUrls: ['./gift-cards-overview.component.less'],
})
export class GiftCardsOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('cardLookup') cardLookup: HTMLElement;
  unsub: Subject<void> = new Subject<void>();
  loading = false;
  gridView: GridDataResult;
  clinicCards: ClinicGiftCards;
  visibleFullIds = new Set<string>();
  sort: SortDescriptor[] = [];
  private gridData: any[] = [];

  constructor(
    private giftCardsService: GiftCardsService,
    private clinicsService: ClinicsService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.clinicsService.clinicIdSelected$
      .pipe(
        takeUntil(this.unsub),
        filter((clinicId) => clinicId != null),
        switchMap((clinicId) => this.giftCardsService.getClinicCards(clinicId))
      )
      .subscribe((clinicCards) => {
        this.clinicCards = clinicCards;
        this.gridData = clinicCards.giftCardDetails.map((gc) => ({
          id: gc.id,
          purchaseDate: gc.purchaseDate,
          sendDate: gc.sendDate,
          deliveryMethod: gc.deliveryMethod,
          deliveryStatus: gc.deliveryStatus,
          purchaserName: gc.purchaser?.fullName,
          recipientName: gc.recipient?.fullName,
          purchaserData: gc.purchaser,
          recipientData: gc.recipient,
          invoiceId: gc.invoiceId,
          balance: gc.balance,
        }));
        this.loadGridData();
      });
  }

  sortChange(sort: SortDescriptor[]) {
    this.sort = sort;
    this.loadGridData();
  }

  loadGridData() {
    this.gridView = {
      data: orderBy(this.gridData, this.sort),
      total: this.gridData.length,
    };
  }

  sortIconClass(column: any) {
    return this.sort.length && this.sort[0].field === column.field && this.sort[0].dir === 'asc'
      ? 'fad fa-sort-up'
      : this.sort.length && this.sort[0].field === column.field && this.sort[0].dir === 'desc'
      ? 'fad fa-sort-down'
      : 'fas fa-sort';
  }

  checkCardBalance() {
    this.modalService.open(this.cardLookup, { centered: true });
  }

  toggleFullId(dataItem: any) {
    if (this.visibleFullIds.has(dataItem.id)) {
      this.visibleFullIds.delete(dataItem.id);
    } else {
      this.visibleFullIds.add(dataItem.id);
    }
  }

  getDisplayId(dataItem: any): string {
    if (this.visibleFullIds.has(dataItem.id)) {
      return GiftCardsService.groupGiftCardId(dataItem.id);
    }
    return GiftCardsService.formatGiftCardId(dataItem.id);
  }

  isFullIdVisible(dataItem: any): boolean {
    return this.visibleFullIds.has(dataItem.id);
  }

  getDeliveryMethodLabel(method?: number): string {
    if (method == null) return 'N/A';
    switch (method) {
      case CommunicationType.Email:
        return 'Email';
      case CommunicationType.SMS:
        return 'SMS';
      default:
        return 'Unknown';
    }
  }

  getDeliveryStatusLabel(status?: number): string {
    if (status == null) return 'N/A';
    switch (status) {
      case CommunicationStatus.Sending:
        return 'Sending';
      case CommunicationStatus.Sent:
        return 'Sent';
      case CommunicationStatus.Fail:
        return 'Failed';
      case CommunicationStatus.Confirmed:
        return 'Confirmed';
      default:
        return 'Unknown';
    }
  }

  getDeliveryStatusClass(status?: number): string {
    if (status == null) return '';
    switch (status) {
      case CommunicationStatus.Sent:
      case CommunicationStatus.Confirmed:
        return 'text-success';
      case CommunicationStatus.Fail:
        return 'text-danger';
      case CommunicationStatus.Sending:
        return 'text-warning';
      default:
        return '';
    }
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
